/**
 * Formats role names by capitalizing the first letter of each word
 * and adding spaces between camelCase/PascalCase words
 * @param {string} str - The string to format
 * @returns {string} Formatted string with proper capitalization and spacing
 */
export const formatRoleName = (str) => {
  if (!str || typeof str !== "string") return "";

  // Add spaces before capital letters (for camelCase/PascalCase)
  const withSpaces = str.replace(/([a-z])([A-Z])/g, "$1 $2");

  // Capitalize first letter of each word
  return withSpaces
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * General text capitalizer for any text
 * @param {string} str - The string to capitalize
 * @returns {string} String with first letter of each word capitalized
 */
export const capitalizeWords = (str) => {
  if (!str) return "";

  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Capitalizes only the first letter of a string, keeping the rest as is
 * @param {string} str - The string to capitalize
 * @returns {string} String with first letter capitalized
 */
export const capitalizeFirstLetter = (str) => {
  if (!str || typeof str !== "string") return "";

  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Capitalizes the first letter and makes the rest lowercase
 * @param {string} str - The string to format
 * @returns {string} String with first letter capitalized and rest lowercase
 */
// export const capitalizeFirstLetterOnly = (str) => {
//   if (!str || typeof str !== "string") return "";

//   return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
// };

export const capitalizeFirstLetterOnly = (str) => {
  if (!str || typeof str !== "string") return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Formats stored notice period values for display.
 * Examples: "immediate" -> "Immediate", "15_days" -> "15 Days"
 * @param {string} value - The stored notice period value
 * @returns {string} Human-readable notice period label
 */
export const formatNoticePeriod = (value) => {
  if (!value || typeof value !== "string") return "";

  return value
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Safely formats enum-like values for display.
 * Examples: "interview_cleared" -> "Interview Cleared", "in-review" -> "In Review"
 * @param {string} value - Raw status/stage value
 * @param {string} fallback - Value returned when formatting is not possible
 * @returns {string} Human-readable label
 */
export const formatStatusOrStage = (value, fallback = "-") => {
  if (value === null || value === undefined) return fallback;
  if (typeof value !== "string") {
    try {
      value = String(value);
    } catch (error) {
      return fallback;
    }
  }

  const sanitizedValue = value.trim();
  if (!sanitizedValue) return fallback;

  return sanitizedValue
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};
